import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import CompositionFooter from '~/containers/patient-list/PatientDrilldown/CompositionFooter';
import DatasetDayGroup from '~/containers/patient-list/PatientDrilldown/DatasetDayGroup';
import { seriesKey } from '~/backend/connector-next-api/seriesKey';
import {
    groupSeriesByDay,
    seriesComposition,
} from '~/containers/patient-list/model';
import {
    allSeriesKeys,
    formatImportMessage,
    selectedSeriesOf,
    selectionSummary,
    skippedText,
    type ClinicalContextField,
    type ImportOutcome,
    type PatientDrop,
    type QueueEntryStatus,
    type Verdict,
} from '../model';
import ClinicalContextFields from './ClinicalContextFields';
import ExistingMatchBar from './ExistingMatchBar';
import ImportIdentityCard from './ImportIdentityCard';
import ResolvedBanner from './ResolvedBanner';

type ImportPatientReviewProps = {
    drop: PatientDrop;
    verdict: Verdict;
    /** This entry's place in the queue. When resolved, the review is read-only and shows a banner. */
    status: QueueEntryStatus;
    /** The commit running for this entry (if any) and the frozen series it was sent against. */
    inFlight?: { readonly seriesKeys: ReadonlySet<string> };
    /** Records the server confirmed touching for this entry. Empty means nothing was sent. */
    landed: readonly ImportOutcome[];
    /** No answer was received, so `landed` does not bound what reached the server. */
    landingUnknown: boolean;
    onRetryCheck: () => void;
    /** Return a skipped entry to the queue for re-review. */
    onUndoSkip: () => void;
    /** Series (by seriesKey) this patient will import; all by default, deselectable per row. */
    checked: ReadonlySet<string>;
    onToggle: (seriesId: string) => void;
    /** Series (by seriesKey) the matched record already holds; undefined until/unless read. */
    alreadyStored?: ReadonlySet<string>;
    notes: string;
    onContextChange: (field: ClinicalContextField, value: string) => void;
};

/** The current queue entry's review: who it is, its dataset tree, and its clinical context. */
export default function ImportPatientReview({
    drop,
    verdict,
    status,
    inFlight,
    landed,
    landingUnknown,
    onRetryCheck,
    onUndoSkip,
    checked,
    onToggle,
    alreadyStored,
    notes,
    onContextChange,
}: ImportPatientReviewProps) {
    const intl = useIntl();
    const resolved = status.kind !== 'waiting';
    const committing = inFlight !== undefined;
    // Only an entry open for review is edited here; resolved and in-flight entries are records.
    const interactive = !resolved && !committing;
    // The tree shows every series under review, else only the frozen/sent subset; the footer follows suit.
    const sentSeriesKeys =
        status.kind === 'imported' ? status.seriesKeys : inFlight?.seriesKeys;
    const treeSeries =
        sentSeriesKeys === undefined
            ? drop.series
            : selectedSeriesOf(drop.series, sentSeriesKeys);
    // The imported mark states a series was sent, so it is used only after the server confirms.
    const treeMode = status.kind === 'imported' ? 'imported' : 'readOnly';
    // A skipped entry describes the whole drop; every other state describes what is or was selected.
    const footer = selectionSummary(
        drop,
        status.kind === 'skipped'
            ? allSeriesKeys(drop.series)
            : (sentSeriesKeys ?? checked)
    );
    // Counted against the tree on screen, not the whole set, so the figure matches what is shown.
    const alreadyStoredCount = treeSeries.filter((s) => {
        const key = seriesKey(s);
        return key !== undefined && alreadyStored?.has(key);
    }).length;
    const existingPatientId =
        verdict.kind === 'resolved' &&
        verdict.target.kind === 'existing-patient'
            ? verdict.target.patientId
            : undefined;

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {status.kind === 'imported' && (
                <ResolvedBanner
                    kind="imported"
                    text={intl.formatMessage({
                        id: 'patientList.import.review.importedBanner',
                    })}
                />
            )}
            {status.kind === 'skipped' && (
                <ResolvedBanner
                    kind="skipped"
                    text={formatImportMessage(
                        intl,
                        skippedText(landed, 'banner', landingUnknown)
                    )}
                    onUndo={onUndoSkip}
                />
            )}

            <ImportIdentityCard
                identity={drop.identity}
                fileCount={drop.instanceCount}
                verdict={resolved ? undefined : verdict}
                onRetryCheck={
                    verdict.kind === 'failed' ? onRetryCheck : undefined
                }
            />

            {!resolved && existingPatientId && (
                <ExistingMatchBar
                    dicomPatientId={drop.identity.dicomPatientId}
                    patientId={existingPatientId}
                />
            )}

            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                {groupSeriesByDay(intl, treeSeries).map((group) =>
                    interactive ? (
                        <DatasetDayGroup
                            key={group.day}
                            group={group}
                            checked={checked}
                            onToggle={onToggle}
                            alreadyStored={alreadyStored}
                        />
                    ) : (
                        <DatasetDayGroup
                            key={group.day}
                            group={group}
                            mode={treeMode}
                        />
                    )
                )}
                {interactive && alreadyStoredCount > 0 && (
                    <ReviewNote>
                        {intl.formatMessage(
                            {
                                id: 'patientList.import.review.alreadyImportedNote',
                            },
                            { count: alreadyStoredCount }
                        )}
                    </ReviewNote>
                )}
            </Box>

            {!resolved && (
                <ClinicalContextFields
                    notes={notes}
                    disabled={committing}
                    onChange={onContextChange}
                />
            )}

            <CompositionFooter
                summary={{
                    composition: seriesComposition(footer.series),
                    studyCount: footer.studyCount,
                    datasetCount: footer.series.length,
                }}
            />
        </Box>
    );
}

/** A quiet line under the dataset tree: what was withheld, and why. */
function ReviewNote({ children }: { children: ReactNode }) {
    return (
        <Typography
            sx={{
                fontSize: 12,
                color: 'text.secondary',
                py: 1,
                px: 2.5,
            }}
        >
            {children}
        </Typography>
    );
}
